import React from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Theme } from "../../../providers/ThemeProvider";
import useTheme from "../../../common/hooks/useTheme";
import WalkthroughCard from "../components/walkthrough/WalkthroughCard";
import { walkthroughs } from "../components/walkthrough/utils";
import { Walkthrough } from "../components/walkthrough/types";
import heroImage from "../images/walkthroughs-hero.png";

/**
 * Walkthroughs landing screen (affiliate `/client/walkthroughs`). A gradient
 * header with title + subtitle sits above a responsive 3-column grid of
 * walkthrough cards. Selecting a card opens its detail screen.
 */
const WalkthroughsScreen = () => {
  const theme: Theme = useTheme();
  const navigate = useNavigate();

  const openWalkthrough = (walkthrough: Walkthrough) => {
    navigate(`/client/walkthroughs/${walkthrough.id}`);
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.hero}>
        <Box component="img" src={heroImage} alt="" sx={styles.heroImage} />
        <Box sx={styles.heroOverlay} />
        <Box sx={styles.heroText}>
          <Box sx={styles.title(theme)}>Walkthroughs</Box>
          <Box sx={styles.subtitle(theme)}>
            Step-by-step video guides to help you master every feature of the
            platform.
          </Box>
        </Box>
      </Box>

      <Box sx={styles.grid}>
        {walkthroughs.map((walkthrough) => (
          <WalkthroughCard
            key={walkthrough.id}
            walkthrough={walkthrough}
            onClick={openWalkthrough}
          />
        ))}
      </Box>
    </Box>
  );
};

export default WalkthroughsScreen;

const styles = {
  root: {
    display: "flex",
    flexDirection: "column" as const,
    width: "100%",
  },
  hero: {
    position: "relative" as const,
    // Pull the banner out of the page wrapper's top (24px) and left (16px)
    // padding so it sits flush to those edges; the right edge stays aligned
    // with the rest of the content.
    marginTop: "-24px",
    marginLeft: "-16px",
    width: "calc(100% + 16px)",
    height: "431px",
    borderRadius: 0,
    overflow: "hidden",
    marginBottom: "24px",
    // ✅ APPROVED: soft ambient banner base behind the photo, no theme token
    // (matches the lavender/blue wash in the Figma hero).
    background:
      "linear-gradient(120deg, #DDE6F5 0%, #E9E6F1 52%, #F4EFF2 100%)",
  },
  heroImage: {
    // Full-bleed: the photo already contains the lavender sky on the left and
    // the subject on the right, so it spans the whole banner.
    position: "absolute" as const,
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    objectPosition: "center 35%",
  },
  heroOverlay: {
    position: "absolute" as const,
    inset: 0,
    // ✅ APPROVED: white-fade scrim from Figma node 15358:54223 — keeps the
    // bottom-left title/subtitle readable over the photo. (white →
    // rgba(255,255,255,0.6) 38.9% → rgba(235,229,229,0.35))
    background:
      "linear-gradient(to top, #FFFFFF 0%, rgba(255,255,255,0.6) 38.9%, rgba(235,229,229,0.35) 100%)",
  },
  heroText: {
    position: "absolute" as const,
    left: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    padding: "40px",
    maxWidth: "560px",
  },
  title: (theme: Theme) => ({
    // Figma hero title is a dark navy — closest theme token is primary-dark P[100].
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "24px",
    lineHeight: "1.4",
  }),
  subtitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "24px",
    maxWidth: "440px",
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
  },
};
